/**
 * 8D Module API - Attachments
 *
 * Database and storage operations for 8D file attachments.
 * Uses mod_8d schema for module isolation.
 */

import { supabase } from '@/integrations/supabase/client';

// =============================================================================
// Types
// =============================================================================

export interface Attachment {
  id: string;
  tenant_id: string;
  claim_id: string;
  file_name: string;
  file_path: string;
  file_size: number | null;
  file_type: string | null;
  label: string | null;
  uploaded_by: string | null;
  uploaded_by_type: string;
  created_at: string;
}

export interface CreateAttachmentInput {
  tenant_id: string;
  claim_id: string;
  file_name: string;
  file_path: string;
  file_size?: number | null;
  file_type?: string | null;
  label?: string | null;
  uploaded_by?: string | null;
  uploaded_by_type: 'factory' | 'supplier';
}

// =============================================================================
// Helpers
// =============================================================================

/**
 * Gets the tenant_id for a claim.
 */
async function getClaimTenantId(claimId: string): Promise<string | null> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('claims')
    .select('tenant_id')
    .eq('id', claimId)
    .single();

  if (error) return null;
  return data?.tenant_id ?? null;
}

// =============================================================================
// Database Queries
// =============================================================================

/**
 * Fetches all attachments for a claim.
 */
export async function fetchAttachmentsByClaim(claimId: string): Promise<Attachment[]> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('attachments')
    .select('*')
    .eq('claim_id', claimId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data;
}

/**
 * Creates an attachment record after file upload.
 */
export async function createAttachment(input: CreateAttachmentInput): Promise<Attachment> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('attachments')
    .insert({
      tenant_id: input.tenant_id,
      claim_id: input.claim_id,
      file_name: input.file_name,
      file_path: input.file_path,
      file_size: input.file_size || null,
      file_type: input.file_type || null,
      label: input.label || null,
      uploaded_by: input.uploaded_by || null,
      uploaded_by_type: input.uploaded_by_type,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Creates an attachment for a claim, auto-fetching tenant_id.
 */
export async function createAttachmentForClaim(
  claimId: string,
  input: Omit<CreateAttachmentInput, 'tenant_id' | 'claim_id'>
): Promise<Attachment> {
  const tenantId = await getClaimTenantId(claimId);
  if (!tenantId) throw new Error('Claim not found');

  return createAttachment({
    tenant_id: tenantId,
    claim_id: claimId,
    ...input,
  });
}

/**
 * Deletes an attachment record.
 */
export async function deleteAttachmentRecord(attachmentId: string): Promise<void> {
  const { error } = await supabase
    .schema('mod_8d')
    .from('attachments')
    .delete()
    .eq('id', attachmentId);

  if (error) throw error;
}

// =============================================================================
// Storage Operations
// =============================================================================

const BUCKET_NAME = '8d-attachments';

/**
 * Uploads a file to the 8D attachments bucket.
 */
export async function uploadAttachmentFile(
  filePath: string,
  file: File
): Promise<string> {
  const { error } = await supabase.storage
    .from(BUCKET_NAME)
    .upload(filePath, file);

  if (error) throw error;
  return filePath;
}

/**
 * Deletes a file from the 8D attachments bucket.
 */
export async function deleteAttachmentFile(filePath: string): Promise<void> {
  const { error } = await supabase.storage
    .from(BUCKET_NAME)
    .remove([filePath]);

  if (error) throw error;
}

/**
 * Gets the public URL for an attachment.
 */
export function getAttachmentPublicUrl(filePath: string): string {
  const { data } = supabase.storage
    .from(BUCKET_NAME)
    .getPublicUrl(filePath);

  return data.publicUrl;
}
